import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  Button,
  Chip,
} from "@mui/material";
import { useSuperAdminContext } from "../providers/SuperAdminProvider";

export const BusinessDetails: React.FC = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const { businesses, businessStats, isLoading } = useSuperAdminContext();

  const business = businesses.find((b) => b.id === businessId);
  const stats = businessStats[businessId || ""];

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (!business) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Business not found</Typography>
        <Button onClick={() => navigate("/super/businesses")} sx={{ mt: 2 }}>
          Back to Business List
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h4">{business.business_name}</Typography>
        <Button
          onClick={() => navigate("/super/businesses")}
          variant="outlined"
        >
          Back to List
        </Button>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Business Information
            </Typography>
            <Divider sx={{ my: 2 }} />

            <Box sx={{ "& > *": { mb: 2 } }}>
              <Box>
                <Typography color="textSecondary" variant="subtitle2">
                  Status
                </Typography>
                <Chip
                  label={business.is_active ? "Active" : "Inactive"}
                  color={business.is_active ? "success" : "error"}
                  size="small"
                />
              </Box>

              <Box>
                <Typography color="textSecondary" variant="subtitle2">
                  Owner
                </Typography>
                <Typography>
                  {business.owner.first_name} {business.owner.last_name}
                </Typography>
                <Typography>{business.owner.email}</Typography>
                <Typography>{business.owner.phone}</Typography>
              </Box>

              {business.contact && (
                <Box>
                  <Typography color="textSecondary" variant="subtitle2">
                    Address
                  </Typography>
                  <Typography>
                    {business.contact.address}
                    {business.contact.city && `, ${business.contact.city}`}
                    {business.contact.state && `, ${business.contact.state}`}
                    {business.contact.country &&
                      `, ${business.contact.country}`}
                    {business.contact.postal_code &&
                      ` ${business.contact.postal_code}`}
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Subscription Details
            </Typography>
            <Divider sx={{ my: 2 }} />

            <Box sx={{ "& > *": { mb: 2 } }}>
              <Box>
                <Typography color="textSecondary" variant="subtitle2">
                  Plan
                </Typography>
                <Typography>{business.subscription.plan}</Typography>
              </Box>

              <Box>
                <Typography color="textSecondary" variant="subtitle2">
                  Status
                </Typography>
                <Chip
                  label={business.subscription.status}
                  color={
                    business.subscription.status === "active"
                      ? "success"
                      : "warning"
                  }
                  size="small"
                />
              </Box>

              <Box>
                <Typography color="textSecondary" variant="subtitle2">
                  Features
                </Typography>
                <Typography>
                  Max Users: {business.subscription.features.max_users}
                </Typography>
                <Typography>
                  Max Products: {business.subscription.features.max_products}
                </Typography>
                <Typography>
                  Max Locations: {business.subscription.features.max_locations}
                </Typography>
                <Typography>
                  Has Inventory:{" "}
                  {business.subscription.features.has_inventory ? "Yes" : "No"}
                </Typography>
                <Typography>
                  Has Analytics:{" "}
                  {business.subscription.features.has_analytics ? "Yes" : "No"}
                </Typography>
                <Typography>
                  Has Customer Loyalty:{" "}
                  {business.subscription.features.has_customer_loyalty
                    ? "Yes"
                    : "No"}
                </Typography>
              </Box>

              <Box>
                <Typography color="textSecondary" variant="subtitle2">
                  Subscription Period
                </Typography>
                <Typography>
                  Start:{" "}
                  {new Date(
                    business.subscription.start_date
                  ).toLocaleDateString()}
                </Typography>
                <Typography>
                  End:{" "}
                  {new Date(
                    business.subscription.end_date
                  ).toLocaleDateString()}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {stats && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Business Statistics
              </Typography>
              <Divider sx={{ my: 2 }} />

              <Grid container spacing={3}>
                <Grid item xs={12} sm={3}>
                  <Typography color="textSecondary" variant="subtitle2">
                    Total Transactions
                  </Typography>
                  <Typography variant="h6">
                    {stats.totalTransactions}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography color="textSecondary" variant="subtitle2">
                    Monthly Revenue
                  </Typography>
                  <Typography variant="h6">${stats.monthlyRevenue}</Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography color="textSecondary" variant="subtitle2">
                    Active Users
                  </Typography>
                  <Typography variant="h6">{stats.activeUsers}</Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography color="textSecondary" variant="subtitle2">
                    Storage Used
                  </Typography>
                  <Typography variant="h6">{stats.storageUsed} GB</Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};
